import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, Lock, CheckCircle2, XCircle, FileBadge, Fingerprint, UserCheck, Building2, Cpu, FileDown, AlertTriangle, Activity, Award } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const tierCls = (t) => t === 1 ? "bg-red-100 text-red-700" : t === 2 ? "bg-amber-100 text-amber-700" : "ek-surface-2 ek-text-muted";

function ModeToggle({ policy, onSet, busy }) {
  const [jur, setJur] = useState(policy.jurisdiction || "");
  return (
    <div data-testid="assurance-mode" className="border ek-line rounded-lg p-3 mb-4">
      <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] mb-2">Assurance mode</div>
      <div className="flex gap-2 flex-wrap items-center">
        {["standard", "regulated"].map((m) => (
          <button key={m} data-testid={`mode-${m}`} disabled={busy} onClick={() => onSet(m, jur)}
            className={`text-[12px] px-3 py-1.5 rounded border capitalize ${policy.mode === m ? "bg-[var(--e360-primary)] text-white border-[var(--e360-primary)]" : "ek-line ek-text-muted hover:bg-zinc-50"}`}>
            {m}
          </button>
        ))}
        <input data-testid="mode-jurisdiction" value={jur} onChange={(e) => setJur(e.target.value)} placeholder="Jurisdiction (e.g. EU / UK)"
          className="text-[12px] border ek-line rounded px-2 py-1.5 w-48" />
      </div>
      <p className="text-[11px] ek-text-muted mt-2">
        {policy.mode === "regulated"
          ? "Regulated: Tier-1 export is blocked without an independent second-line validation or client counter-signature."
          : "Standard: Tier-1 export requires four-eyes sign-off. Independent second line is optional."}
      </p>
    </div>
  );
}

function GateBanner({ gate }) {
  const ok = gate.allowed;
  return (
    <div data-testid="export-gate" className={`rounded-lg p-3 mb-4 border ${ok ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`}>
      <div className="flex items-center gap-2 text-[13px] font-medium ek-text">
        {ok ? <CheckCircle2 size={15} className="text-emerald-600" /> : <Lock size={15} className="text-amber-600" />}
        {gate.message}
      </div>
      <div className="text-[11px] ek-text-muted mt-1">{gate.tier1_complete}/{gate.tier1_total} Tier-1 artefacts carry a complete model card · mode: {gate.mode}</div>
    </div>
  );
}

function Card({ card, onSecondLine, onClientSign, busy }) {
  const approved = card.hitl?.decision === "approved";
  return (
    <div data-testid={`model-card-${card.artefact}`} className="border ek-line rounded-lg p-3">
      <div className="flex items-center justify-between gap-2 mb-2">
        <span className="text-[13px] font-semibold ek-text inline-flex items-center gap-1.5"><FileBadge size={14} /> {card.label}</span>
        <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${tierCls(card.tier)}`}>Tier {card.tier}</span>
      </div>
      <div className="flex items-center gap-1.5 text-[12px] mb-2">
        {card.complete ? <><CheckCircle2 size={14} className="text-emerald-600" /> <span className="text-emerald-700">Model card complete</span></>
          : <><XCircle size={14} className="text-amber-600" /> <span className="text-amber-700">{card.blocking_reasons[0]}</span></>}
      </div>
      <div className="text-[11px] ek-text-muted space-y-0.5">
        <div>HITL: {card.hitl?.reviewer || "—"} ({card.hitl?.decision || "pending"})</div>
        {card.second_line && <div>Second line: {card.second_line.by} · {card.second_line.decision}</div>}
        {card.client_signoff && <div>Client counter-sign: {card.client_signoff.by}</div>}
        <div>Confidence: {card.confidence?.score ?? "—"} {card.confidence?.band ? `(${card.confidence.band})` : ""}</div>
        <div className="font-mono text-[10px] ek-text-muted truncate">Seal: {card.seal_hash?.slice(0, 24)}…</div>
        <div className="ek-text-muted">Anchor: PRA SS1/23 {card.regulatory_anchor.pra_ss1_23.join(",")} · ISO 42001 cl.{card.regulatory_anchor.iso_42001.join(",")}</div>
      </div>
      {approved && !card.complete && (
        <div className="flex gap-2 mt-2">
          <button data-testid={`second-line-${card.artefact}`} disabled={busy} onClick={() => onSecondLine(card.artefact)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border border-[var(--e360-primary)] text-[var(--e360-primary)] hover:bg-[var(--e360-primary)]/5">
            <UserCheck size={12} /> Attest second line
          </button>
          <button data-testid={`client-sign-${card.artefact}`} disabled={busy} onClick={() => onClientSign(card.artefact)}
            className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded border ek-line ek-text-muted hover:bg-zinc-50">
            <Building2 size={12} /> Client counter-sign
          </button>
        </div>
      )}
      <button data-testid={`card-pdf-${card.artefact}`} onClick={() => onPdf(card.artefact)}
        className="mt-2 inline-flex items-center gap-1 text-[11px] text-[var(--e360-primary)] hover:underline">
        <FileDown size={12} /> Download model card
      </button>
    </div>
  );
}

export default function AssuranceMRM({ clientId }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verify, setVerify] = useState(null);
  const [aiCalls, setAiCalls] = useState(null);
  const [monitor, setMonitor] = useState(null);
  const [cert, setCert] = useState(null);

  const load = useCallback(() => {
    api.get(`/admin/enterprise360/clients/${clientId}/model-cards`).then(({ data }) => setData(data)).catch(() => {});
    api.get(`/admin/enterprise360/clients/${clientId}/ai-calls`).then(({ data }) => setAiCalls(data)).catch(() => {});
    api.get(`/admin/enterprise360/clients/${clientId}/model-monitor`).then(({ data }) => setMonitor(data)).catch(() => {});
    api.get(`/admin/enterprise360/clients/${clientId}/assurance-certificate`).then(({ data }) => setCert(data)).catch(() => {});
  }, [clientId]);

  const certPdf = useCallback(async () => {
    try {
      const res = await api.get(`/admin/enterprise360/clients/${clientId}/assurance-certificate.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "Assurance_Certificate.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download certificate"); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const cardPdf = useCallback(async (artefact) => {
    try {
      const res = await api.get(`/admin/enterprise360/clients/${clientId}/model-cards/${artefact}.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `ModelCard_${artefact}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download card"); }
  }, [clientId]);

  const setMode = useCallback(async (mode, jurisdiction) => {
    setBusy(true);
    try { await api.put(`/admin/enterprise360/clients/${clientId}/assurance-policy`, { mode, jurisdiction }); load(); }
    catch { toast.error("Could not update mode"); }
    setBusy(false);
  }, [clientId, load]);

  const secondLine = useCallback(async (artefact) => {
    setBusy(true);
    try { await api.post(`/admin/enterprise360/clients/${clientId}/signoffs/${artefact}/second-line`, { decision: "validated", note: "Independent validation" }); toast.success("Second-line validation recorded"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  }, [clientId, load]);

  const clientSign = useCallback(async (artefact) => {
    const name = window.prompt("Client reviewer name (counter-signatory):");
    if (!name) return;
    setBusy(true);
    try { await api.post(`/admin/enterprise360/clients/${clientId}/signoffs/${artefact}/client-countersign`, { client_reviewer: name }); toast.success("Client counter-signature recorded"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  }, [clientId, load]);

  const runVerify = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get(`/admin/enterprise360/clients/${clientId}/evidence-trail/verify`); setVerify(data); }
    catch { toast.error("Verification failed"); }
    setBusy(false);
  }, [clientId]);

  if (!data) return <div className="text-sm ek-text-muted py-4">Loading governance…</div>;

  return (
    <div data-testid="assurance-mrm">
      <p className="text-[12px] ek-text-muted max-w-2xl mb-4">
        <ShieldCheck size={12} className="inline mr-1" /> Model Risk Management (SS1/23-aligned). Every publishable artefact carries a tiered model card auto-assembled from the sign-off record, confidence and the sealed evidence trail. Tier-1 export is gated; the bar rises in Regulated mode.
      </p>

      <ModeToggle policy={data.policy} onSet={setMode} busy={busy} />

      {cert && (
        <div data-testid="assurance-certificate" className="rounded-lg p-3 mb-4 border border-[var(--e360-primary)]/40 bg-[var(--e360-primary)]/[0.04]">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[12px] font-semibold ek-text inline-flex items-center gap-1.5"><Award size={14} /> Assurance certificate</span>
            <button data-testid="cert-pdf" onClick={certPdf}
              className="inline-flex items-center gap-1 text-[11px] tracking-[0.1em] uppercase text-[var(--e360-primary)] border border-[var(--e360-primary)] px-2 py-1 rounded hover:bg-[var(--e360-primary)]/5">
              <FileDown size={12} /> Download
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[12px]">
            <div><div className="text-[10px] ek-text-muted uppercase tracking-[0.1em]">Mode</div><div className="font-semibold capitalize ek-text">{cert.policy.mode}</div></div>
            <div><div className="text-[10px] ek-text-muted uppercase tracking-[0.1em]">Export</div><div className={`font-semibold ${cert.gate.allowed ? "text-emerald-700" : "text-red-700"}`}>{cert.gate.allowed ? "Permitted" : "Blocked"}</div></div>
            <div><div className="text-[10px] ek-text-muted uppercase tracking-[0.1em]">Chain</div><div className={`font-semibold ${cert.chain.valid ? "text-emerald-700" : "text-red-700"}`}>{cert.chain.valid ? "Intact" : "Unverified"}</div></div>
            <div><div className="text-[10px] ek-text-muted uppercase tracking-[0.1em]">Model risk</div><div className={`font-semibold uppercase ${cert.monitor.state === "suspend" ? "text-red-700" : cert.monitor.state === "watch" ? "text-amber-700" : cert.monitor.state === "active" ? "text-emerald-700" : "ek-text-muted"}`}>{cert.monitor.state}</div></div>
            {cert.model_risk?.score != null && (
              <div data-testid="cert-posture"><div className="text-[10px] ek-text-muted uppercase tracking-[0.1em]">Board posture</div><div className={`font-semibold uppercase ${cert.model_risk.rag === "red" ? "text-red-700" : cert.model_risk.rag === "amber" ? "text-amber-700" : "text-emerald-700"}`}>{cert.model_risk.score}/100 {cert.model_risk.rag}</div></div>
            )}
          </div>
        </div>
      )}

      <GateBanner gate={data.export_gate} />

      <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-[var(--e360-primary)] mb-2">Model cards</div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2 mb-4">
        {data.cards.map((c) => <Card key={c.artefact} card={c} onSecondLine={secondLine} onClientSign={clientSign} onPdf={cardPdf} busy={busy} />)}
      </div>

      <div data-testid="ai-supply-chain" className="border ek-line rounded-lg p-3 mb-4">
        <div className="text-[12px] font-semibold ek-text inline-flex items-center gap-1.5 mb-2"><Cpu size={14} /> AI supply chain — logged generative calls</div>
        {aiCalls && aiCalls.count > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead><tr className="ek-text-muted text-left"><th className="py-1 pr-3">When (UTC)</th><th className="pr-3">Prompt</th><th className="pr-3">Model</th><th className="pr-3">In→Out hash</th></tr></thead>
              <tbody>
                {aiCalls.calls.map((c) => (
                  <tr key={c.call_id} className="border-t ek-line">
                    <td className="py-1 pr-3 ek-text-muted">{(c.at || "").slice(0, 16).replace("T", " ")}</td>
                    <td className="pr-3">{c.prompt_id} v{c.prompt_version}</td>
                    <td className="pr-3">{c.model_provider}/{c.model_name}</td>
                    <td className="pr-3 font-mono text-[10px] ek-text-muted">{(c.input_hash || "").slice(0, 8)}→{(c.output_hash || "").slice(0, 8)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <p className="text-[11px] ek-text-muted">No generative AI calls logged yet. When the AI steering narrative is generated, each call is stamped with its versioned prompt, model and input/output hashes here.</p>}
      </div>

      {monitor && (
        <div data-testid="pause-triggers" className="border ek-line rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-[12px] font-semibold ek-text inline-flex items-center gap-1.5"><Activity size={14} /> Quarterly audit — pause triggers</span>
            <span data-testid="monitor-state" className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded font-semibold ${monitor.state === "suspend" ? "bg-red-100 text-red-700" : monitor.state === "watch" ? "bg-amber-100 text-amber-700" : monitor.state === "active" ? "bg-emerald-100 text-emerald-700" : "ek-surface-2 ek-text-muted"}`}>{monitor.state}</span>
          </div>
          <p className="text-[11px] ek-text-muted mb-2">{monitor.message}</p>
          <div className="space-y-1">
            {monitor.triggers.map((t) => (
              <div key={t.id} className="flex items-start gap-2 text-[12px]">
                {t.status === "breached" ? <XCircle size={14} className="text-red-600 mt-0.5 shrink-0" />
                  : t.status === "watch" ? <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0" />
                  : t.status === "ok" ? <CheckCircle2 size={14} className="text-emerald-600 mt-0.5 shrink-0" />
                  : <span className="w-3.5 h-3.5 rounded-full border ek-line mt-0.5 shrink-0" />}
                <span className="ek-text-soft"><b>{t.name}</b>{t.threshold ? <span className="ek-text-muted"> (≤{t.threshold})</span> : ""} — <span className="ek-text-muted">{t.detail}</span></span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border ek-line rounded-lg p-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-[12px] font-semibold ek-text inline-flex items-center gap-1.5"><Fingerprint size={14} /> Evidence-chain integrity</span>
          <button data-testid="verify-integrity" onClick={runVerify} disabled={busy}
            className="text-[12px] px-3 py-1.5 rounded bg-[var(--e360-primary)] text-white disabled:opacity-40">Verify integrity</button>
        </div>
        {verify ? (
          <div data-testid="verify-result" className="text-[12px] space-y-1">
            <div className="flex items-center gap-1.5">
              {verify.chain.valid ? <CheckCircle2 size={14} className="text-emerald-600" /> : <XCircle size={14} className="text-red-600" />}
              <span className={verify.chain.valid ? "text-emerald-700" : "text-red-700"}>{verify.chain.message}</span>
            </div>
            <div className="ek-text-muted">{verify.count} sealed events · {verify.seal_snapshots} seal snapshot(s) recorded</div>
            <div className="font-mono text-[10px] ek-text-muted break-all">Head: {verify.head_hash}</div>
          </div>
        ) : <p className="text-[11px] ek-text-muted">Recompute the hash chain — each event seals the previous, so any altered record breaks the chain. Head hashes are logged over time as a seal history (external public anchoring is a later increment).</p>}
      </div>
    </div>
  );
}
